import { useNavigate, useLocation } from "react-router-dom";
import React, { useState, useEffect, useRef } from 'react';
import { useCommon } from "../context/CommonContext";
import { useLanguage } from "../context/LanguageProvider";

const IMG2 = () => {
    /**
     * Pulls in the current route information, including any state passed
     * from the previous screen (such as selected/unselected colors).
     */
    const location = useLocation();

    // Access context-provided configuration/state
    const { kioskTimeoutText } = useCommon(); // Text to show after inactivity timeout
    const { translation } = useLanguage();   // Function for translating keys to strings

    /**
     * Retrieve colors passed via router state:
     * - selectedColors: those to react to
     * - unSelectedColors: distractor colors that should be ignored
     * Default to empty arrays if missing.
     */
    const selectedColors = location.state?.selectedColors || [];
    const unSelectedColors = location.state?.unSelectedColors || [];

    /**
     * When the component mounts or when selected/unselected colors change:
     * - If no selected colors are provided, redirect back to the color selection ("/IMGI2").
     * - Log the received color lists for debugging.
     */
    useEffect(() => {
        if (selectedColors.length === 0) {
            navigate("/IMGI2");
        }
        console.log("Selected colors in IMG2:", selectedColors);
        console.log("unSelected colors in IMG2:", unSelectedColors);
        // Game logic will use these colors later.
    }, [selectedColors, unSelectedColors]);

    /**
     * Configuration object controlling timing, word lists, and reaction constraints.
     */
    const config = {
        words: selectedColors,              // Stimuli that require a reaction
        invalidWords: unSelectedColors,     // Distractors that should *not* be reacted to
        minReactionTime: 1,                 // Any reaction faster than this is "too early"
        maxReactionTime: 1000,               // Any reaction slower than this is "too slow"
                // preparationTime: 3000,
        // delayBetweenWords: 200,
        // delayedPlusMinus: 25,
        totalWords: 1000,                   // Cap on number of stimuli
        delayBetweenWordsMin: 1000,         // Minimum delay between stimuli after a transition
        delayBetweenWordsMax: 1500,         // Maximum delay between stimuli
        startGameTime: 1000,                // Delay before first word
        inactivityTime: 240000              // Timeout for inactivity (ms) — 4 minutes
    };

    /**
     * ========== STATE VARIABLES ==========
     */
    const [isRunning, setIsRunning] = useState(false);       // Game active/inactive
    const [displayText, setDisplayText] = useState('');      // Current word/color displayed
    const [message, setMessage] = useState('');              // General instruction or feedback
    const [results, setResults] = useState([]);              // List of reaction results/objects
    const [wordsDisplayed, setWordsDisplayed] = useState(0); // Number of stimuli presented
        // const [showNextButton, setShowNextButton] = useState(false);
    // const [showResults, setShowResults] = useState(false);
    const [isWordDisplaying, setIsWordDisplaying] = useState(false); // True while a stimulus is on-screen
    const [isTransitioning, setIsTransitioning] = useState(false);   // True during inter-stimulus delay
    const [isInactive, setIsInactive] = useState(false);     // True when inactivity timeout is reached
    const [message1, setMessage1] = useState('');            // First line of error feedback
    const [message2, setMessage2] = useState('');            // Second line of error feedback
    const [isKeyTrackingDisabled, setIsKeyTrackingDisabled] = useState(false); // Block input after success

    /**
     * ========== REFS (MUTABLE OBJECTS THAT PERSIST) ==========
     * Used for mutable game state that should not trigger re-renders.
     */
    const consecutiveGoodReactionsRef = useRef(0); // Number of correct consecutive reactions
    const shuffledWordsRef = useRef([]);           // Randomized list of all stimuli
    const currentWordIndexRef = useRef(0);         // Index into shuffled list
    const allWords = config.words.concat(config.invalidWords); // Combined stimuli+distractors
    const startTimeRef = useRef(null);             // Timestamp for stimulus onset
    const wordTimeoutRef = useRef(null);           // Timeout for auto-handling missed reactions
    const currentWordRef = useRef(null);           // Currently displayed stimulus
    const delaywordRef = useRef(null);             // Timeout for scheduling next word
    const inactivityTimerRef = useRef(null);       // Timeout for inactivity handling

    const navigate = useNavigate();

    /**
     * Randomly shuffles an array in-place (Fisher-Yates shuffle) and returns it.
     */
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    /**
     * Start the game after an initial delay (config.startGameTime).
     * This runs only once on mount.
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            start();
        }, config.startGameTime);
        return () => clearTimeout(timer);
    }, []);

    /**
     * Display an instructional or error message to the user.
     * If isError=true, also set translation-specific feedback lines.
     */
    const showMessage = (text, isError = false) => {
        console.log(`showMessage called with text: "${text}", isError: ${isError}`);
        setMessage(text);
        setMessage1('');
        setMessage2('');
        if (isError) {
            setMessage1(translation("IMG2_mistakeMessage"));
            setMessage2(translation("IMG2_focusReminder"));
        }
    };

    /**
     * Core function to present the next word/color stimulus to the player.
     * Handles both correct-target and distractor stimuli, schedules next display
     * or ends game if missed reaction.
     */
    const displayWord = () => {
        console.log(`displayWord called. Words displayed: ${wordsDisplayed} , shuffled array - ${shuffledWordsRef.current}`);
        if (wordsDisplayed < config.totalWords) {
            const currentWord = shuffledWordsRef.current[currentWordIndexRef.current];
            currentWordRef.current = currentWord;
            setDisplayText(currentWord);
            setIsWordDisplaying(true);
            setIsTransitioning(false);
            startTimeRef.current = Date.now();
            console.log(`Displaying Color: "${currentWord}"`);

            // Setup maximum reaction time timeout
            wordTimeoutRef.current = setTimeout(() => {
                const isInvalidWord = config.invalidWords.includes(currentWordRef.current);
                if (!isInvalidWord) {
                    // Player failed to respond to a valid target word => error
                    console.log("too slow message from displayWord");
                    const errorResult = {
                        Color: currentWordRef.current,
                        reactionTime: 'Error: Missed '
                    };
                    setResults(prev => [...prev, errorResult]);
                    let missedMessage = translation("IMG2_missedInstruction") + config.words;
                    showMessage(missedMessage, true);
                    end(true);
                } else {
                    // Distractor passed without reaction: increment and move on
                    setWordsDisplayed(prev => prev + 1);
                    currentWordIndexRef.current += 1;
                    setIsTransitioning(true);
                    setDisplayText('');
                                        // const randomDelay = config.delayBetweenWords + (Math.random() < 0.5 ? -config.delayedPlusMinus : config.delayedPlusMinus);
                    const randomDelay2 = (Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) + config.delayBetweenWordsMin);
                    console.log(`random delay ${randomDelay2}`);
                                        //(Math.random()*(5000-300)+300)
                    setTimeout(() => {
                        displayWord();
                    }, randomDelay2);
                }
            }, config.maxReactionTime);

        } else {
            console.log('All words displayed. Ending the game.');
            end(false);
        }
    };

    /**
     * Initializes a new game round:
     * - Shuffles stimuli
     * - Resets counters/states
     * - Clears messages
     * - Starts presentation loop
     */
    const start = () => {
        console.log('Game started.');
        shuffledWordsRef.current = shuffleArray(allWords);
        currentWordIndexRef.current = 0;
        setIsRunning(true);
                // setResults([]);
        setWordsDisplayed(1);
        setDisplayText('');
                // setShowNextButton(false);
        // setShowResults(false);
        showMessage('');
                // let countdown = Math.ceil(config.preparationTime / 1000);
        // const countdownInterval = setInterval(() => {
        //     if (countdown > 0) {
        //         setDisplayText(countdown.toString());
        //         console.log(`Countdown: ${countdown}`);
        //         countdown--;
        //     } else {
        //         clearInterval(countdownInterval);
        //         displayWord();
        //     }
        // }, 1000);
        displayWord();
    };

        // const next = () => {
    //     console.log('Navigating to next page.');
    //     navigate('/IATDef');
    // };

    /**
     * Ends the current game session.
     * If badReaction is true: reset consecutive good counter.
     * If goodReaction streak meets target: disable input and move to next stage.
     */
    const end = (badReaction) => {
        clearTimeout(delaywordRef.current);
        setWordsDisplayed(0);
        console.log('Game ended.');
        setIsRunning(false);
                // setShowNextButton(true);
        setIsTransitioning(false);
        if (wordTimeoutRef.current) {
            clearTimeout(wordTimeoutRef.current);
        }
        setDisplayText('');
                // setShowResults(true);

        console.log(badReaction);
        console.log(consecutiveGoodReactionsRef.current);
        if (badReaction) {
            consecutiveGoodReactionsRef.current = 0;
        } else {
            if (consecutiveGoodReactionsRef.current >= 3) {
                setIsKeyTrackingDisabled(true);
                console.log(isKeyTrackingDisabled);
                showMessage("");
                navigate('/IATDef');
            }
        }
    };

    /**
     * Handles SPACE key presses to control the game flow and submit reactions.
     * - Ignores presses if input disabled or during transition.
     * - Starts game if not running yet.
     * - Records reaction time and passes to response handler.
     */
    const handleSpacePress = (event) => {
        console.log('Space key pressed.');
        resetInactivityTimer();
        if (isKeyTrackingDisabled) {
            console.log('Key tracking is disabled. Ignoring key press.');
            return;
        }
        if (event.code === 'Space') {
            event.preventDefault();
            if (isTransitioning) {
                console.log('Transitioning, ignoring space key.');
                return;
            }
            if (!isRunning) {
                console.log('Starting the game.');
                start();
            } else if (startTimeRef.current && isWordDisplaying) {
                setIsWordDisplaying(false);
                const reactionTime = Date.now() - startTimeRef.current;
                console.log(`Reaction time recorded: ${reactionTime}ms`);
                handleResponse(reactionTime);
            }
        }
    };

    /**
     * Resets inactivity timeout. If timeout elapses, displays inactivity overlay.
     */
    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        setIsInactive(false);
        inactivityTimerRef.current = setTimeout(() => {
            setIsInactive(true);
        }, config.inactivityTime);
    };

    /**
     * Evaluates a reaction:
     * - Checks correctness: reacting to valid word and within allowed times.
     * - Records errors and calls end() if error.
     * - On success, advances the game or ends after required streak length.
     */
    const handleResponse = (reactionTime) => {
        console.log(`handleResponse called with reactionTime: ${reactionTime}`);
        if (wordTimeoutRef.current) {
            clearTimeout(wordTimeoutRef.current);
        }
        const isInvalidWord = config.invalidWords.includes(currentWordRef.current);

        if (isInvalidWord) {
            // Wrongly reacted to distractor
            const errorResult = {
                Color: currentWordRef.current,
                reactionTime: 'Error: Wrong reaction '
            };
            setResults(prev => [...prev, errorResult]);
            let colorMessage = translation("IMG2_wrongColorInstruction") + config.words;
            showMessage(colorMessage, true);
            end(true);

        } else if (reactionTime < config.minReactionTime) {
            // Pressed too early
            showMessage(translation("IMG2_tooEarlyMessage"), true);
            end(true);

        } else if (reactionTime > config.maxReactionTime) {
            // Pressed too late
            showMessage(translation("IMG2_tooSlowMessage"), true);
            end(true);

        } else {
            // Correct reaction
            const newResults = [...results, {
                Color: currentWordRef.current,
                reactionTime: reactionTime
            }];
            setResults(newResults);
            console.log(`New result added: ${JSON.stringify(newResults[newResults.length - 1])}`);
                        // showMessage(`Good Match! Reaction time: ${reactionTime}ms`);
            setDisplayText('');
            setWordsDisplayed(prev => prev + 1);
            setIsTransitioning(true);

            // After a random delay, either cycle to next word or end after streak
                        // const randomDelay = config.delayBetweenWords + (Math.random() < 0.5 ? -config.delayedPlusMinus : config.delayedPlusMinus);
            // console.log(`random delay ${randomDelay}`);
            // //(Math.random()*(5000-300)+300)
            // setTimeout(() => {
            //     displayWord();
            // }, randomDelay);
            const randomDelay2 = (Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) + config.delayBetweenWordsMin);
            console.log(`random delay ${randomDelay2}`);
            delaywordRef.current = setTimeout(() => {
                if (isRunning) {
                    consecutiveGoodReactionsRef.current += 1;
                    console.log(`consecutive - ${consecutiveGoodReactionsRef.current}`);
                    if (consecutiveGoodReactionsRef.current >= 3) {
                        end(false);
                    } else {
                        currentWordIndexRef.current += 1;
                        displayWord();
                    }
                }
            }, randomDelay2);
        }
    };

    /**
     * Set up and clean up global event listeners:
     * - Keyboard listener for SPACE presses
     * - Block SPACE from scrolling
     * - Mouse movement resets inactivity timer
     * Also starts inactivity timer on mount.
     */
    useEffect(() => {
        document.addEventListener('keydown', handleSpacePress);
        const preventScroll = (e) => {
            if (e.code === 'Space' && e.target === document.body) {
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', preventScroll);

        resetInactivityTimer();

        const handleMouseMove = () => {
            resetInactivityTimer();
        };
        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            document.removeEventListener('keydown', handleSpacePress);
            window.removeEventListener('keydown', preventScroll);
            window.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(inactivityTimerRef.current);
        };
    }, [isRunning, isWordDisplaying, isTransitioning]);

    /**
     * Render:
     * - Feedback messages
     * - The current stimulus
     * - Inactivity overlay if applicable
     */
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
            <div className="text-center p-6">
                                {/* <h1 className="text-2xl font-bold mb-4">Practice Game - 2</h1>
                <p className="mb-4">Press the space bar each time a word appears<br />and press the space bar to start the test!</p> */}
                {/* Error/Feedback message area */}
                <div className="text-red-600 mb-8 text-left font-bold left-0 text-3xl">
                    {message1}
                </div>
                {message && (
                    <ul className="list-disc mb-8 w-full ml-4 pl-2 text-left text-2xl font-medium">
                        <li>{message}</li>
                    </ul>
                )}
                <div className="mb-7 text-left left-0 text-2xl font-medium">
                    {message2}
                </div>

                {/* Main display area for the current word/color stimulus */}
                <div className="fixed inset-0 flex items-center justify-center text-6xl">
                    {displayText}
                </div>

                {/* Inactivity Timeout Overlay */}
                {isInactive && (
                    <div className="fixed inset-0 flex items-center justify-center bg-yellow-200">
                        <p className="text-5xl font-bold">{kioskTimeoutText.current}</p>
                    </div>
                )}
                  {/* Buttons */}
                {/* {showNextButton && (
                    <div className="flex justify-center space-x-4">
                        <button 
                            onClick={next} 
                            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition duration-200"
                        >
                            Next
                        </button>
                    </div>
                )} */}

                {/* Results Area */}
                {/* {showResults && results.length > 0 && (
                    <div className="mt-6 bg-white p-6 rounded-lg shadow-md">
                        <h2 className="text-xl font-semibold mb-4">This screen is currently used for testing purpose, will be removed in the final version</h2>
                        <h2 className="text-xl font-semibold mb-4">Your Results:</h2>
                        <ul>
                            {results.map((result, index) => (
                                <li key={index}>
                                    Color: {result.Color}, Reaction Time: {result.reactionTime}ms
                                </li>
                            ))}
                        </ul>
                    </div>
                )} */}
            </div>
        </div>
    );
};

export default IMG2;